import React, { useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'
import { GovernanceInlineFormPanel } from '../governance/GovernanceInlineFormPanel'
import { API_ENDPOINTS } from '../../config/api'
import { featureFlags } from '../../config/featureFlags'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

export type PolicyEditTarget = {
  id: string
  title: string
  code?: string
  type: string
  status: string
  department?: string
  ownerName?: string
  ownerEmail?: string
  effectiveFrom?: string
  effectiveTo?: string
  reviews?: Array<{ plannedDate: string }>
}

type Props = {
  open: boolean
  policy: PolicyEditTarget | null
  onClose: () => void
  onSuccess: () => void
}

const GOVERNANCE_TYPES = [
  'POLICY',
  'PROCEDURE',
  'GUIDELINE',
  'INSTRUCTION',
  'PLAN',
  'STANDARD',
  'GOVERNANCE_RECORD',
  'OTHER',
] as const

function toDateInputValue(iso?: string) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

export function PolicyDocumentEditModal({ open, policy, onClose, onSuccess }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    title: '',
    code: '',
    type: 'POLICY',
    status: 'Draft',
    department: '',
    ownerName: '',
    ownerEmail: '',
    effectiveFrom: '',
    nextReview: '',
    description: '',
    version: '',
    changeNote: '',
  })
  const [newFile, setNewFile] = useState<File | null>(null)

  useEffect(() => {
    if (!open || !policy) return
    const nextReview = policy.reviews?.[0]?.plannedDate || policy.effectiveTo
    setForm({
      title: policy.title || '',
      code: policy.code || '',
      type: String(policy.type || 'POLICY').toUpperCase(),
      status: policy.status || 'Draft',
      department: policy.department || '',
      ownerName: policy.ownerName || '',
      ownerEmail: policy.ownerEmail || '',
      effectiveFrom: toDateInputValue(policy.effectiveFrom),
      nextReview: toDateInputValue(nextReview),
      description: '',
      version: '',
      changeNote: '',
    })
    setNewFile(null)
    setError(null)
  }, [open, policy])

  if (!open || !policy) return null

  const typeLabelMap: Record<string, string> = {
    POLICY: t('governance.policies.form.typePolicy', 'Policy'),
    PROCEDURE: t('governance.policies.form.typeProcedure', 'Procedure'),
    GUIDELINE: t('governance.policies.form.typeGuideline', 'Guideline'),
    INSTRUCTION: t('governance.policies.form.typeInstruction', 'Instruction'),
    PLAN: t('governance.policies.form.typePlan', 'Plan'),
    STANDARD: t('governance.policies.form.typeStandard', 'Standard'),
    GOVERNANCE_RECORD: t('governance.policies.form.typeGovernanceRecord', 'Governance record'),
    OTHER: t('governance.evidenceArtifacts.other', 'Other'),
  }
  const typeOptions = GOVERNANCE_TYPES.map((value) => ({
    value,
    label: typeLabelMap[value] || value,
  }))

  const statusOptions = [
    { value: 'Draft', label: t('governance.policies.form.statusDraft', 'Draft') },
    { value: 'UnderReview', label: t('governance.policies.form.statusUnderReview', 'Under Review') },
    { value: 'Approved', label: t('governance.policies.form.statusApproved', 'Approved') },
    { value: 'Deprecated', label: t('governance.policies.form.statusDeprecated', 'Deprecated') },
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !form.title.trim()) return

    setLoading(true)
    setError(null)
    try {
      const lifecycle = featureFlags.DOCUMENT_LIFECYCLE_V1
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        code: form.code || null,
        type: form.type,
        status: form.status,
        department: form.department || null,
        ownerName: form.ownerName || null,
        ownerEmail: form.ownerEmail || null,
        effectiveFrom: form.effectiveFrom || null,
      }
      if (lifecycle && form.nextReview) {
        payload.nextReviewAt = form.nextReview
        payload.effectiveTo = form.nextReview
      } else if (form.nextReview) {
        payload.effectiveTo = form.nextReview
      }
      if (lifecycle && form.description) payload.description = form.description

      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICY(policy.id), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Failed to save document')

      if (newFile) {
        const fd = new FormData()
        fd.append('file', newFile)
        if (form.version) fd.append('version', form.version)
        if (form.changeNote) fd.append('changeNote', form.changeNote)
        if (form.effectiveFrom) fd.append('effectiveFrom', form.effectiveFrom)
        if (form.nextReview) fd.append('nextReviewAt', form.nextReview)
        const vRes = await fetch(API_ENDPOINTS.GOVERNANCE_POLICY_VERSION_UPLOAD(policy.id), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        })
        const vData = await vRes.json().catch(() => ({}))
        if (!vRes.ok) throw new Error(vData.error || 'Failed to upload new version')
      }

      onSuccess()
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setLoading(false)
    }
  }

  return (
    <GovernanceInlineFormPanel
      title={t('governance.documentLifecycle.editDocument', 'Edit document')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="policy-document-edit-form"
            color="primary"
            size="small"
            disabled={loading || !form.title.trim()}
          >
            {loading ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
        </>
      }
    >
      <form id="policy-document-edit-form" onSubmit={handleSubmit} className={listStyles.inlineFormBody}>
        {error ? <div className={listStyles.formError}>{error}</div> : null}

        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel}>{t('governance.policies.form.title', 'Title')}</label>
          <input
            className={listStyles.formInput}
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div className={listStyles.formGrid2}>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>{t('governance.policies.form.code', 'Code')}</label>
            <input
              className={listStyles.formInput}
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </div>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>{t('governance.policies.form.type', 'Document type')}</label>
            <Select<string>
              minWidth="100%"
              options={typeOptions}
              value={form.type}
              onChange={(v) => setForm({ ...form, type: v || 'POLICY' })}
            />
          </div>
        </div>

        <div className={listStyles.formGrid2}>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>{t('governance.policies.form.status', 'Status')}</label>
            <Select<string>
              minWidth="100%"
              options={statusOptions}
              value={form.status}
              onChange={(v) => setForm({ ...form, status: v || 'Draft' })}
            />
          </div>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>{t('governance.policies.form.department', 'Department')}</label>
            <input
              className={listStyles.formInput}
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
            />
          </div>
        </div>

        <div className={listStyles.formGrid2}>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>{t('governance.policies.form.ownerName', 'Owner')}</label>
            <input
              className={listStyles.formInput}
              value={form.ownerName}
              onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
            />
          </div>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>{t('governance.policies.form.ownerEmail', 'Owner email')}</label>
            <input
              type="email"
              className={listStyles.formInput}
              value={form.ownerEmail}
              onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
            />
          </div>
        </div>

        <div className={listStyles.formGrid2}>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>
              {t('governance.documentLifecycle.effectiveFrom', 'Effective From')}
            </label>
            <input
              type="date"
              className={listStyles.formInput}
              value={form.effectiveFrom}
              onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
            />
          </div>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>
              {t('governance.documentLifecycle.nextReview', 'Next Review')}
            </label>
            <input
              type="date"
              className={listStyles.formInput}
              value={form.nextReview}
              onChange={(e) => setForm({ ...form, nextReview: e.target.value })}
            />
            <p className={listStyles.formFieldHint}>
              {t('governance.documentLifecycle.effectiveToHint', 'Effective To in the table uses this date.')}
            </p>
          </div>
        </div>

        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel}>
            {t('governance.documentLifecycle.uploadNewVersion', 'Upload new version')}
          </label>
          <input
            type="file"
            accept=".pdf,.docx,.doc,.txt"
            className={listStyles.formInput}
            onChange={(e) => setNewFile(e.target.files?.[0] || null)}
          />
          <p className={listStyles.formFieldHint}>
            {t(
              'governance.documentLifecycle.uploadNewVersionHint',
              'Optional. Previous file versions remain available for download.',
            )}
          </p>
        </div>

        {newFile ? (
          <div className={listStyles.formGrid2}>
            <div className={listStyles.formField}>
              <label className={listStyles.formFieldLabel}>
                {t('governance.policies.upload.version', 'Version')}
              </label>
              <input
                className={listStyles.formInput}
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="v1.1"
              />
            </div>
            <div className={listStyles.formField}>
              <label className={listStyles.formFieldLabel}>
                {t('governance.documentLifecycle.changeNote', 'Change note')}
              </label>
              <input
                className={listStyles.formInput}
                value={form.changeNote}
                onChange={(e) => setForm({ ...form, changeNote: e.target.value })}
              />
            </div>
          </div>
        ) : null}
      </form>
    </GovernanceInlineFormPanel>
  )
}
